import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["input", "monthYear", "daysGrid"]
  static values = {
    format: { type: String, default: "yyyy-mm-dd" },
    initial: { type: String, default: "" }
  }

  connect() {
    // Check for initial value from attribute or input
    const initialValue = this.initialValue || this.inputTarget.value

    if (initialValue) {
      const date = new Date(initialValue)
      if (!isNaN(date.getTime())) {
        this.currentDate = date
        this.selectedDate = date
        this.inputTarget.value = this.formatDate(date)
      } else {
        this.currentDate = new Date()
        this.selectedDate = null
      }
    } else {
      this.currentDate = new Date()
      this.selectedDate = null
    }

    this.viewDate = new Date(this.currentDate)
    this.isOpen = false

    this.createCalendar()
    this.updateDisplay()

    // Close on outside click
    this.boundHandleOutsideClick = this.handleOutsideClick.bind(this)
    document.addEventListener("click", this.boundHandleOutsideClick)
  }

  disconnect() {
    document.removeEventListener("click", this.boundHandleOutsideClick)
    if (this.calendar) {
      this.calendar.remove()
    }
  }

  createCalendar() {
    const calendar = document.createElement("div")
    calendar.className = "date-picker-calendar hidden absolute z-50 mt-1 bg-white rounded-lg shadow-lg border border-gray-200 p-4 w-72"
    calendar.innerHTML = `
      <div class="flex items-center justify-between mb-4">
        <button type="button" data-action="click->date-picker#prevMonth" class="p-1.5 hover:bg-gray-100 rounded-lg transition-colors">
          <svg class="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7"/>
          </svg>
        </button>
        <span data-date-picker-target="monthYear" class="text-sm font-semibold text-gray-900"></span>
        <button type="button" data-action="click->date-picker#nextMonth" class="p-1.5 hover:bg-gray-100 rounded-lg transition-colors">
          <svg class="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7"/>
          </svg>
        </button>
      </div>
      <div class="grid grid-cols-7 gap-1 mb-2">
        <div class="text-center text-xs font-medium text-gray-500 py-1">Su</div>
        <div class="text-center text-xs font-medium text-gray-500 py-1">Mo</div>
        <div class="text-center text-xs font-medium text-gray-500 py-1">Tu</div>
        <div class="text-center text-xs font-medium text-gray-500 py-1">We</div>
        <div class="text-center text-xs font-medium text-gray-500 py-1">Th</div>
        <div class="text-center text-xs font-medium text-gray-500 py-1">Fr</div>
        <div class="text-center text-xs font-medium text-gray-500 py-1">Sa</div>
      </div>
      <div data-date-picker-target="daysGrid" class="grid grid-cols-7 gap-1"></div>
      <div class="flex items-center justify-between mt-4 pt-3 border-t border-gray-100">
        <button type="button" data-action="click->date-picker#clear" class="text-sm text-gray-500 hover:text-gray-700 font-medium">Clear</button>
        <button type="button" data-action="click->date-picker#today" class="text-sm text-indigo-600 hover:text-indigo-700 font-medium">Today</button>
      </div>
    `
    this.element.appendChild(calendar)
    this.calendar = calendar
  }

  toggle(e) {
    e.preventDefault()
    e.stopPropagation()
    this.isOpen ? this.close() : this.open()
  }

  open() {
    this.isOpen = true
    this.calendar.classList.remove("hidden")
    this.updateDisplay()
  }

  close() {
    this.isOpen = false
    this.calendar.classList.add("hidden")
  }

  handleOutsideClick(e) {
    if (!this.element.contains(e.target)) {
      this.close()
    }
  }

  prevMonth(e) {
    e.preventDefault()
    e.stopPropagation()
    this.viewDate.setMonth(this.viewDate.getMonth() - 1)
    this.updateDisplay()
  }

  nextMonth(e) {
    e.preventDefault()
    e.stopPropagation()
    this.viewDate.setMonth(this.viewDate.getMonth() + 1)
    this.updateDisplay()
  }

  today(e) {
    e.preventDefault()
    e.stopPropagation()
    this.selectDate(new Date())
  }

  clear(e) {
    e.preventDefault()
    e.stopPropagation()
    this.selectedDate = null
    this.inputTarget.value = ""
    this.inputTarget.dispatchEvent(new Event("change", { bubbles: true }))
    this.close()
  }

  selectDate(date) {
    this.selectedDate = date
    this.viewDate = new Date(date)
    this.inputTarget.value = this.formatDate(date)
    this.inputTarget.dispatchEvent(new Event("change", { bubbles: true }))
    this.updateDisplay()
    this.close()
  }

  updateDisplay() {
    const months = ["January", "February", "March", "April", "May", "June",
                    "July", "August", "September", "October", "November", "December"]

    if (this.hasMonthYearTarget) {
      this.monthYearTarget.textContent = `${months[this.viewDate.getMonth()]} ${this.viewDate.getFullYear()}`
    }

    if (this.hasDaysGridTarget) {
      this.renderDays()
    }
  }

  renderDays() {
    const year = this.viewDate.getFullYear()
    const month = this.viewDate.getMonth()

    const firstDay = new Date(year, month, 1)
    const lastDay = new Date(year, month + 1, 0)
    const startDay = firstDay.getDay()
    const totalDays = lastDay.getDate()

    const today = new Date()
    today.setHours(0, 0, 0, 0)

    let html = ""

    // Previous month days
    const prevMonth = new Date(year, month, 0)
    const prevMonthDays = prevMonth.getDate()
    for (let i = startDay - 1; i >= 0; i--) {
      const day = prevMonthDays - i
      html += `<button type="button" class="date-day-btn p-2 text-center text-sm text-gray-300 rounded-lg cursor-default" disabled>${day}</button>`
    }

    // Current month days
    for (let day = 1; day <= totalDays; day++) {
      const date = new Date(year, month, day)
      date.setHours(0, 0, 0, 0)

      const isToday = date.getTime() === today.getTime()
      const isSelected = this.selectedDate &&
        date.getTime() === new Date(this.selectedDate.getFullYear(), this.selectedDate.getMonth(), this.selectedDate.getDate()).getTime()

      let classes = "date-day-btn p-2 text-center text-sm rounded-lg transition-colors cursor-pointer "

      if (isSelected) {
        classes += "bg-indigo-600 text-white font-medium hover:bg-indigo-700"
      } else if (isToday) {
        classes += "bg-indigo-100 text-indigo-700 font-medium hover:bg-indigo-200"
      } else {
        classes += "text-gray-700 hover:bg-gray-100"
      }

      html += `<button type="button" class="${classes}" data-action="click->date-picker#pickDay" data-day="${day}">${day}</button>`
    }

    // Next month days
    const remainingCells = 42 - (startDay + totalDays)
    for (let day = 1; day <= remainingCells; day++) {
      html += `<button type="button" class="date-day-btn p-2 text-center text-sm text-gray-300 rounded-lg cursor-default" disabled>${day}</button>`
    }

    this.daysGridTarget.innerHTML = html
  }

  pickDay(e) {
    e.preventDefault()
    e.stopPropagation()
    const day = parseInt(e.currentTarget.dataset.day)
    const date = new Date(this.viewDate.getFullYear(), this.viewDate.getMonth(), day)
    this.selectDate(date)
  }

  formatDate(date) {
    const year = date.getFullYear()
    const month = String(date.getMonth() + 1).padStart(2, "0")
    const day = String(date.getDate()).padStart(2, "0")
    return `${year}-${month}-${day}`
  }

  formatDateForInput(date) {
    const months = ["January", "February", "March", "April", "May", "June",
                    "July", "August", "September", "October", "November", "December"]
    return `${months[date.getMonth()]} ${date.getDate()}, ${date.getFullYear()}`
  }

  // Allow typing in the input
  handleInput(e) {
    const value = e.target.value
    if (value) {
      const date = new Date(value)
      if (!isNaN(date.getTime())) {
        this.selectedDate = date
        this.viewDate = new Date(date)
      }
    }
  }
}
